import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";

import { getDiscountReport } from "../../../apis/customTemplate";
import { toastify } from "../../../helperFunctions/toastify";
import DateInput from "../../SharedComponents/DateInput";
import { setTableSearch } from "../../../redux/slices/commonSlice";

import {
  customDateSort,
  downloadCsv,
  formatDate,
  getInvoiceId,
} from "../../../helperFunctions/utils";
import { toast } from "react-toastify";

const FilterWrapper = styled.div`
  label {
    color: inherit;
  }

  .btn-wrapper {
    align-items: center;
    display: flex;
    margin-top: 20px;
  }
`;

function RefundReportView() {
  const numberFormatter = Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const buttonRef = useRef(null);
  const pathname = location.pathname;
  const fieldPathname = pathname.replace("/", "");

  const { tableSearch } = useSelector((state) => state.commonData);

  const [filteredData, setFilteredData] = useState([]);
  const [revenueList, setRevenueList] = useState([]);
  const [startDate, setStartDate] = useState(
    tableSearch[fieldPathname]?.refundL?.startDate
      ? tableSearch[fieldPathname]?.refundL?.startDate
      : `${new Date().getFullYear()}-01-01`
  );
  const [endDate, setEndDate] = useState(
    tableSearch[fieldPathname]?.refundL?.endDate
      ? tableSearch[fieldPathname]?.refundL?.endDate
      : `${new Date().getFullYear()}-12-29`
  );
  const [refetch, setRefetch] = useState(false);

  const onReduxFilter = (valueStartDate, valueEndDate) => {
    dispatch(
      setTableSearch({
        ...tableSearch,
        [fieldPathname]: {
          ...tableSearch[fieldPathname],
          refundL: {
            startDate: valueStartDate,
            endDate: valueEndDate,
          },
        },
      })
    );
  };

  useEffect(() => {
    setStartDate(
      tableSearch[fieldPathname]?.refundL?.startDate
        ? tableSearch[fieldPathname]?.refundL?.startDate
        : `${new Date().getFullYear()}-01-01`
    );
    setEndDate(
      tableSearch[fieldPathname]?.refundL?.endDate
        ? tableSearch[fieldPathname]?.refundL?.endDate
        : `${new Date().getFullYear()}-12-29`
    );

    if (
      buttonRef.current &&
      (tableSearch[fieldPathname]?.refundL?.startDate ||
        tableSearch[fieldPathname]?.refundL?.endDate)
    ) {
      setTimeout(() => {
        if (buttonRef.current) {
          buttonRef.current.click();
        }
      }, [1000]);
    }
  }, []);

  useEffect(() => {
    getDiscountReport({
      start_date: startDate,
      end_date: endDate,
    }).then((res) => {
      console.log("res?.result?.data", res);
      if (res?.result?.data) {
        setRevenueList(res.result.data);
        setFilteredData(res.result.data);
      } else {
        toast.error(res?.error?.message || "Something went wrong");
      }
    });
  }, [refetch]);

  // eslint-disable-next-line no-sparse-arrays
  const columns = [
    {
      name: "Date",
      selector: (row) => row.created_at,
      sortable: true,
      sortFunction: (a, b) => customDateSort(a, b, "created_at"),
      cell: (row) => formatDate(row.created_at),
    },
    {
      name: "Invoice ID",
      selector: (row) => row.invoiceNo || 0,
      cell: (row) => getInvoiceId(row?.invoiceNo),
    },
    {
      name: "Patient Name",
      selector: (row) => row.patient?.patientProfile?.fullName,
    },
    {
      name: "Product Discount",
      selector: (row) => 0,
      width: "200px",
      cell: (row) => {
        if (row.product?.length === 0) {
          return "-";
        }

        return (
          <div>
            {
              row.product.map((rowProduct, rowIdx) => {
                let discValue = "";
                if (
                  rowProduct.product_discountType === "Amount" ||
                  rowProduct.product_discountType === null
                ) {
                  discValue = `$${numberFormatter.format(
                    rowProduct.product_discount
                  )}`;
                } else {
                  discValue = `${numberFormatter.format(
                    rowProduct.product_discount
                  )}%`;
                }
                return (
                  <div key={rowIdx} style={{ display: "block" }}>{rowProduct.name} - {discValue}</div>
                );
              })
            }
          </div>
        )
      },
    },
    {
      name: "Treatment Discount",
      selector: (row) => 0,
      width: "200px",
      cell: (row) => {
        if (row.treatment?.length === 0) {
          return "-";
        }

        return (
          <div>
            {
              row.treatment.map((rowTreatment, rowIdx) => {
                let discValue = "";
                if (
                  rowTreatment.service_discountType === "Amount" ||
                  rowTreatment.service_discountType === null
                ) {
                  discValue = `$${numberFormatter.format(
                    rowTreatment.service_discount
                  )}`;
                } else {
                  discValue = `${numberFormatter.format(
                    rowTreatment.service_discount
                  )}%`;
                }

                return (
                  <div key={rowIdx} style={{ display: "block" }}>{rowTreatment.name} - {discValue}</div>
                );
              })
            }
          </div>
        )
      },
    },
    {
      name: "Package Discount",
      selector: () => 0,
      width: "200px",
      cell: (row) => {
        if (row.package?.length === 0) {
          return "-";
        }

        return (
          <div>
            {
              row.package.map((rowPackage, rowIdx) => {
                let discValue = "";
                if (
                  rowPackage.package_discountType === "Amount" ||
                  rowPackage.package_discountType === "Amount"
                ) {
                  discValue = `$${numberFormatter.format(
                    rowPackage.package_discount
                  )}`;
                } else {
                  discValue = `${numberFormatter.format(
                    rowPackage.package_discount
                  )}%`;
                }
                return (
                  <div key={rowIdx} style={{ display: "block" }}>{rowPackage.name} - {discValue}</div>
                );
              })
            }
          </div>
        )
      },
    },
    {
      name: "Invoice Discount",
      selector: (row) => row.promo || 0,
      cell: (row) => {
        if (row.promoType === "Amount") {
          return numberFormatter.format(row.promo);
        }

        return `${row.promo}%`;
      },
    },
    {
      name: "Total Amount",
      selector: (row) => row.gst,
      cell: (row) => numberFormatter.format(row.gst),
    },
  ];

  const data = filteredData?.map((e, index) => {
    return {
      ...e,
      index,
      key: index,
    };
  });

  const customStyles = {
    headCells: {
      style: {
        justifyContent: "center",
      },
    },
    cells: {
      style: {
        justifyContent: "center",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row,
      style: {
        borderWidth: "0px",
      },
    },
    {
      when: (row) => row.index % 2 === 1,
      style: {
        backgroundColor: "#f8f8f8",
        borderWidth: "0px",
      },
    },
  ];

  const exportCsv = () => {
    const csvData = revenueList.map((row) => {
      let productDiscounts = "";
      let treatmentDiscounts = "";
      let packageDiscounts = "";

      row.product.map((rowProduct) => {
        productDiscounts += `${rowProduct.name} - ${rowProduct.product_discountType === "Amount" ? numberFormatter.format(rowProduct.product_discount) : `${numberFormatter.format(rowProduct.product_discount)}%`}, `;
      });

      row.treatment.map((rowTreatment) => {
        treatmentDiscounts += `${rowTreatment.name} - ${rowTreatment.service_discountType === "Amount" ? numberFormatter.format(rowTreatment.service_discount) : `${numberFormatter.format(rowTreatment.service_discount)}%`}, `;
      });

      row.package.map((rowPackage) => {
        packageDiscounts += `${rowPackage.name} - ${rowPackage.package_discountType === "Amount" ? numberFormatter.format(rowPackage.package_discount) : `${numberFormatter.format(rowPackage.package_discount)}%`}, `;
      });

      return {
        Date: formatDate(row.created_at),
        "Patient Name": row.patient?.patientProfile?.fullName,
        "Invoice ID": getInvoiceId(row?.invoiceNo),
        "Package Discounts": productDiscounts,
        "Treatment Discounts": treatmentDiscounts,
        "Product Discounts": packageDiscounts,
        "Invoice Discount": row.promoType === "Amount" ? row.promo : `${row.promo}%`,
        "Total Amount": row.gst
      };
    });

    downloadCsv(csvData, "Revenue_Report");
  };

  return (
    <div className="p-0">
      <div
        className="mainHeading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "18px",
          }}
        >
          <FilterWrapper className="d-flex">
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={startDate}
                label="Start Date"
                onChange={(e) => {
                  setStartDate(e);
                }}
              />
            </div>
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={endDate}
                label="End Date"
                onChange={(e) => {
                  setEndDate(e);
                }}
              />
            </div>
            <div className="btn-wrapper">
              <Button
                ref={buttonRef}
                className="addItem me-3"
                onClick={() => {
                  if (endDate < startDate) {
                    toastify("End date is less than start date", "error");
                    return;
                  }
                  onReduxFilter(startDate, endDate);
                  setRefetch(!refetch);
                }}
              >
                Search
              </Button>
              <Button className="addItem" onClick={() => exportCsv()}>
                Export
              </Button>
            </div>
          </FilterWrapper>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        pagination
        customStyles={customStyles}
        conditionalRowStyles={conditionalRowStyles}
      />
    </div>
  );
}

export default RefundReportView;
